import { NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  inject
} from '@angular/core';
import { User } from '@angular/fire/auth';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router, RouterModule } from '@angular/router';
import { Ga4Service } from '@vaki/shared/util/ga4';
import {
  BRANDS_IDS,
  Image,
  Language,
  Vaki,
  VakiAnalytics,
  VakiSiteId,
  VakiState
} from '@vaki/shared/util/interfaces';
import { VakersShellUiToolbarLogoComponent } from '@vaki/vakers/shell/ui/toolbar-logo';
import { VakersShellUiUserMenuComponent } from '@vaki/vakers/shell/ui/user-menu';
import { NavigationToolbar } from '@vaki/vakers/shell/util';
import { VakersVakisUiShareBoxComponent } from '@vaki/vakers/vakis/ui/share-box';
import { Subject, debounceTime, takeUntil, tap } from 'rxjs';

const matModules = [
  MatMenuModule,
  MatIconModule,
  MatToolbarModule,
  MatSlideToggleModule,
  MatBadgeModule,
  MatFormFieldModule,
  MatInputModule,
  MatButtonModule
];
const angularModules = [
  RouterModule,
  NgIf,
  NgSwitch,
  NgSwitchCase,
  NgSwitchDefault,
  ReactiveFormsModule
];

@Component({
  selector: 'lib-vakers-shell-ui-toolbar',
  imports: [
    ...angularModules,
    ...matModules,
    VakersShellUiToolbarLogoComponent,
    VakersShellUiUserMenuComponent,
    VakersVakisUiShareBoxComponent
  ],
  templateUrl: './vakers-shell-ui-toolbar.component.html',
  styleUrl: './vakers-shell-ui-toolbar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VakersShellUiToolbarComponent implements OnInit, OnDestroy {
  @Input({ required: true }) userLoggedIn!: Signal<boolean>;
  @Input() toolbar: NavigationToolbar | null = NavigationToolbar.MAIN;
  @Input() backUrl: string | null = '/';
  @Input() user: User | null = null;
  @Input() currentLanguage: string | undefined | null;
  @Input() currentCurrency: string | null = null;
  @Input() logo: Image | undefined | null;
  @Input() currentYear = new Date().getFullYear();
  @Input() isOwner = false;
  @Input() isOpen = false;
  @Input() vaki: Vaki | undefined | null;
  @Input() vakiAnalytics: VakiAnalytics | undefined | null;
  @Input() editMode: boolean | null = false;
  @Input() hasRewards: boolean | undefined | null;
  @Input() hasSubscriptionPlans: boolean | undefined | null;
  @Input() cartTotalRewards: number | undefined | null;
  @Input() currencies: string[] | null = null;
  @Input() languages: Language[] | undefined;
  @Input() isVakiSite: boolean | null | undefined;
  @Input() siteId: VakiSiteId | null | undefined;
  @Input() siteLogo: Image | undefined | null;

  @Output() toggleEditMode = new EventEmitter();
  @Output() languageSelected = new EventEmitter<string>();
  @Output() toggleMenu = new EventEmitter();
  @Output() logout = new EventEmitter();
  @Output() openCartBottomSheet = new EventEmitter();
  @Output() openSupportDialog = new EventEmitter<{
    vaki: Vaki;
    available: boolean;
    currentCurrency: string;
    currencies: string[];
  }>();
  @Output() searchKeyword = new EventEmitter<string>();

  private router: Router = inject(Router);

  showSubToolbar = false;

  ga4Service = inject(Ga4Service);

  NavigationToolbar = NavigationToolbar;
  VakiState = VakiState;
  BRANDS_IDS = BRANDS_IDS;

  destroy$ = new Subject();
  searchTerm = new FormControl<string>('', { nonNullable: true });

  ngOnInit(): void {
    this.searchTerm.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        tap((keyword: string) => this.searchKeyword.emit(keyword))
      )
      .subscribe();
  }
  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  changeEditMode(): void {
    this.toggleEditMode.emit();
    this.editMode = !this.editMode;
  }

  support(): void {
    if (!this.hasRewards && !this.hasSubscriptionPlans) {
      const available =
        this.vakiAnalytics?.state === this.VakiState.PUBLICADA ||
        this.vakiAnalytics?.state === this.VakiState.CREADA ||
        this.vakiAnalytics?.state === this.VakiState.VERIFICADA;
      this.openSupportDialog.emit({
        vaki: this.vaki as Vaki,
        available,
        currentCurrency: this.currentCurrency as string,
        currencies: this.currencies as string[]
      });
    } else {
      this.router.navigate(this.getSupportUrl());
    }
  }

  getSupportUrl(): string[] {
    if (this.hasRewards || this.hasSubscriptionPlans) {
      return ['vaki', this.vaki?.info?.url ?? '', 'recompensas'];
    }
    return ['vaki', this.vaki?.info?.url ?? ''];
  }

  openCart(): void {
    this.openCartBottomSheet.emit();
  }

  resetInput(): void {
    this.searchTerm.patchValue('');
  }
}
